import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail, MessageSquare, Phone, Check, X, Edit2, Save,
  ChevronDown, ChevronRight, ToggleLeft, ToggleRight,
  Smartphone, Clock, Info, AlertCircle
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';

// ── Types ──────────────────────────────────────────────────────────────────

type NotificationChannel = 'email' | 'text' | 'voice';
type TextTab = 'appointment' | 'telehealth' | 'document' | 'cancellation' | 'rescheduled' | 'noshow' | 'postsession' | 'balance';

interface AutomatedEmail {
  id: string;
  label: string;
  description?: string;
  enabled: boolean;
  editable: boolean;
  category: 'automated' | 'reminder' | 'billing';
}

interface TextTemplate {
  id: string;
  tab: TextTab;
  label: string;
  content: string;
  variables: string[];
  maxLength: number;
}

interface NotificationSettings {
  // Email settings
  automatedEmails: Record<string, boolean>;
  reminderEmailsEnabled: boolean;
  reminderEmailTiming: number; // hours before
  // Text settings
  textRemindersEnabled: boolean;
  textReminderTiming: number; // hours before
  textTemplates: Record<string, string>;
  // Voice settings (simplified — we use Hope AI)
  voiceRemindersEnabled: boolean;
  voiceReminderTiming: number;
}

// ── Default Data ───────────────────────────────────────────────────────────

const DEFAULT_AUTOMATED_EMAILS: AutomatedEmail[] = [
  // Automated emails
  { id: 'welcome', label: 'Welcome', enabled: true, editable: true, category: 'automated' },
  { id: 'appointment_confirmation', label: 'Appointment confirmation', enabled: true, editable: true, category: 'automated' },
  { id: 'appointment_confirmation_docs', label: 'Appointment confirmation with documents', enabled: true, editable: true, category: 'automated' },
  { id: 'video_appointment_confirmation', label: 'Video appointment confirmation', enabled: true, editable: true, category: 'automated' },
  { id: 'video_appointment_confirmation_docs', label: 'Video appointment confirmation with documents', enabled: true, editable: true, category: 'automated' },
  { id: 'appointment_declined_new', label: 'Appointment declined (new client)', enabled: true, editable: true, category: 'automated' },
  { id: 'appointment_declined_existing', label: 'Appointment declined (existing client)', enabled: true, editable: true, category: 'automated' },
  { id: 'client_portal_activation', label: 'Client portal activation', enabled: true, editable: true, category: 'automated' },
  { id: 'document_request', label: 'Document request', enabled: true, editable: true, category: 'automated' },
  { id: 'recurring_measure_request', label: 'Recurring measure request', enabled: true, editable: true, category: 'automated' },
  { id: 'new_billing_document', label: 'New billing document available', enabled: true, editable: true, category: 'automated' },
  { id: 'invoice_past_due', label: 'Invoice past due', enabled: true, editable: true, category: 'automated' },
  { id: 'appointment_request_received', label: 'Appointment request received', enabled: false, editable: true, category: 'automated' },
  { id: 'pending_appointment_request', label: 'Pending appointment request', enabled: false, editable: true, category: 'automated' },
  { id: 'ad_hoc_document_reminder', label: 'Ad hoc document reminder', enabled: true, editable: true, category: 'automated' },
  { id: 'contact_form_inquiry', label: 'Contact form inquiry confirmation', enabled: true, editable: true, category: 'automated' },
  { id: 'out_of_office_inquiry', label: 'Out of office contact form inquiry', enabled: false, editable: true, category: 'automated' },
  { id: 'appointment_rescheduled', label: 'Appointment rescheduled', enabled: true, editable: true, category: 'automated' },
  { id: 'noshow_followup', label: 'Missed appointment follow-up', enabled: true, editable: true, category: 'automated' },
  { id: 'post_session_followup', label: 'Post-session follow-up', enabled: true, editable: true, category: 'automated' },
  // Reminder emails
  { id: 'appointment_reminder', label: 'Appointment reminder', enabled: true, editable: true, category: 'reminder' },
  { id: 'telehealth_reminder', label: 'Telehealth appointment reminder', enabled: true, editable: true, category: 'reminder' },
  { id: 'starting_soon', label: 'Starting soon (15 minutes)', enabled: true, editable: true, category: 'reminder' },
  { id: 'document_completion_reminder', label: 'Document completion reminder', enabled: true, editable: true, category: 'reminder' },
  { id: 'cancellation_message', label: 'Cancellation message', enabled: true, editable: true, category: 'reminder' },
  // Billing document emails
  { id: 'default_invoice', label: 'Default invoice emails', enabled: true, editable: true, category: 'billing' },
  { id: 'default_statement', label: 'Default statement emails', enabled: true, editable: true, category: 'billing' },
  { id: 'default_superbill', label: 'Default superbill emails', enabled: true, editable: true, category: 'billing' },
  { id: 'balance_reminder', label: 'Outstanding balance reminder', enabled: true, editable: true, category: 'billing' },
];

const DEFAULT_TEXT_TEMPLATES: TextTemplate[] = [
  {
    id: 'appointment_reminder',
    tab: 'appointment',
    label: 'Appointment reminder',
    content: 'Reminder for {client_first_name}\'s appointment on {appointment_date} at {appointment_time} with {practice_name}.',
    variables: ['{client_first_name}', '{appointment_date}', '{appointment_time}', '{practice_name}', '{provider_name}', '{confirm_cancel_link}'],
    maxLength: 500,
  },
  {
    id: 'telehealth_reminder',
    tab: 'telehealth',
    label: 'Telehealth reminder',
    content: 'Reminder for {client_first_name}\'s video appointment on {appointment_date} at {appointment_time} with {practice_name}. Join your session from your Client Portal.',
    variables: ['{client_first_name}', '{appointment_date}', '{appointment_time}', '{practice_name}', '{provider_name}', '{session_link}'],
    maxLength: 500,
  },
  {
    id: 'document_reminder',
    tab: 'document',
    label: 'Document completion reminder',
    content: 'This is a reminder that you have documents to complete before your appointment on {appointment_date} at {appointment_time} with {practice_name}.\n\nThe documents include:\n{document_request_names}\n\nSign in to your Client Portal to get started.\n{client_portal_login_link}',
    variables: ['{client_first_name}', '{appointment_date}', '{appointment_time}', '{practice_name}', '{document_request_names}', '{client_portal_login_link}'],
    maxLength: 500,
  },
  {
    id: 'cancellation_single',
    tab: 'cancellation',
    label: 'Single appointment cancellation',
    content: 'The appointment on {appointment_date} at {appointment_time} has been cancelled by {clinician_full_name}. No appointment fees will be charged.',
    variables: ['{appointment_date}', '{appointment_time}', '{clinician_full_name}', '{practice_name}'],
    maxLength: 500,
  },
  {
    id: 'rescheduled',
    tab: 'rescheduled',
    label: 'Appointment rescheduled',
    content: '{practice_name}: Your appointment has been rescheduled to {appointment_date} at {appointment_time} with {provider_name}. Details: {client_portal_login_link}',
    variables: ['{client_first_name}', '{appointment_date}', '{appointment_time}', '{provider_name}', '{practice_name}', '{client_portal_login_link}'],
    maxLength: 500,
  },
  {
    id: 'noshow_followup',
    tab: 'noshow',
    label: 'Missed appointment follow-up',
    content: '{practice_name}: We missed you at your appointment on {appointment_date}. Please contact us to reschedule. {practice_phone}',
    variables: ['{client_first_name}', '{appointment_date}', '{appointment_time}', '{provider_name}', '{practice_name}', '{practice_phone}'],
    maxLength: 500,
  },
  {
    id: 'post_session',
    tab: 'postsession',
    label: 'Post-session follow-up',
    content: '{practice_name}: Thank you for your visit with {provider_name}. Check your Client Portal for any follow-up materials. {client_portal_login_link}',
    variables: ['{client_first_name}', '{provider_name}', '{practice_name}', '{client_portal_login_link}'],
    maxLength: 500,
  },
  {
    id: 'balance_reminder',
    tab: 'balance',
    label: 'Outstanding balance reminder',
    content: '{practice_name}: You have an outstanding balance of {balance_amount}. Please log in to view details or make a payment. {client_portal_login_link}',
    variables: ['{client_first_name}', '{balance_amount}', '{practice_name}', '{client_portal_login_link}'],
    maxLength: 500,
  },
];

const DEFAULT_SETTINGS: NotificationSettings = {
  automatedEmails: {},
  reminderEmailsEnabled: true,
  reminderEmailTiming: 48,
  textRemindersEnabled: true,
  textReminderTiming: 24,
  textTemplates: {},
  voiceRemindersEnabled: false,
  voiceReminderTiming: 24,
};

// ── Component ──────────────────────────────────────────────────────────────

const ClientNotifications: React.FC = () => {
  const { user } = useAuth();
  const [channel, setChannel] = useState<NotificationChannel>('email');
  const [settings, setSettings] = useState<NotificationSettings>(DEFAULT_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  // Email state
  const [expandedSection, setExpandedSection] = useState<string>('automated');

  // Text state
  const [textTab, setTextTab] = useState<TextTab>('appointment');
  const [editingTemplate, setEditingTemplate] = useState<string | null>(null);
  const [templateDraft, setTemplateDraft] = useState('');

  // ── Load from Firestore ────────────────────────────────────────────────

  useEffect(() => {
    if (!user?.uid) return;
    (async () => {
      try {
        const ref = doc(db, 'notification_settings', user.uid);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          setSettings({ ...DEFAULT_SETTINGS, ...snap.data() as NotificationSettings });
        }
      } catch (err) {
        console.error('Failed to load notification settings:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.uid]);

  // ── Save to Firestore ──────────────────────────────────────────────────

  const saveSettings = useCallback(async (newSettings: NotificationSettings) => {
    if (!user?.uid) return;
    setSaving(true);
    try {
      const ref = doc(db, 'notification_settings', user.uid);
      await setDoc(ref, { ...newSettings, updatedAt: new Date().toISOString() }, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save notification settings:', err);
    } finally {
      setSaving(false);
    }
  }, [user?.uid]);

  // ── Email toggle handler ───────────────────────────────────────────────

  const toggleEmail = (emailId: string) => {
    const newEmails = { ...settings.automatedEmails, [emailId]: !getEmailEnabled(emailId) };
    const newSettings = { ...settings, automatedEmails: newEmails };
    setSettings(newSettings);
    saveSettings(newSettings);
  };

  const getEmailEnabled = (emailId: string): boolean => {
    if (emailId in settings.automatedEmails) return settings.automatedEmails[emailId];
    const defaultEmail = DEFAULT_AUTOMATED_EMAILS.find(e => e.id === emailId);
    return defaultEmail?.enabled ?? true;
  };

  // ── Text template handler ──────────────────────────────────────────────

  const getTemplateContent = (templateId: string): string => {
    if (templateId in (settings.textTemplates || {})) return settings.textTemplates[templateId];
    const defaultTemplate = DEFAULT_TEXT_TEMPLATES.find(t => t.id === templateId);
    return defaultTemplate?.content || '';
  };

  const saveTemplate = (templateId: string, content: string) => {
    const newTemplates = { ...settings.textTemplates, [templateId]: content };
    const newSettings = { ...settings, textTemplates: newTemplates };
    setSettings(newSettings);
    saveSettings(newSettings);
    setEditingTemplate(null);
  };

  // ── Render phone preview ───────────────────────────────────────────────

  const renderPhonePreview = (content: string) => {
    // Replace variables with sample values
    const preview = content
      .replace(/\{client_first_name\}/g, 'Jamie')
      .replace(/\{appointment_date\}/g, 'Monday, March 10th')
      .replace(/\{appointment_time\}/g, '9:00 am (ET)')
      .replace(/\{practice_name\}/g, 'My ClinicCore')
      .replace(/\{provider_name\}/g, 'Dr. Richardson')
      .replace(/\{clinician_full_name\}/g, 'Dr. Richardson')
      .replace(/\{confirm_cancel_link\}/g, '')
      .replace(/\{session_link\}/g, 'https://app.mycliniccore.pro/session')
      .replace(/\{document_request_names\}/g, 'Demographics Form\nConsent for Treatment')
      .replace(/\{client_portal_login_link\}/g, 'https://app.mycliniccore.pro/login');

    return (
      <div style={{
        width: '260px',
        backgroundColor: '#1a1a2e',
        borderRadius: '24px',
        padding: '16px',
        border: '3px solid #333',
        flexShrink: 0,
      }}>
        {/* Phone notch */}
        <div style={{
          width: '80px', height: '6px', backgroundColor: '#333',
          borderRadius: '3px', margin: '0 auto 16px auto',
        }} />
        {/* Message bubble */}
        <div style={{
          backgroundColor: '#1e3a5f',
          borderRadius: '12px',
          padding: '14px',
          fontSize: '13px',
          lineHeight: '1.5',
          color: '#e2e8f0',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}>
          {preview}
        </div>
        <div style={{ fontSize: '11px', color: '#64748b', textAlign: 'center', marginTop: '8px' }}>
          Preview with sample data
        </div>
      </div>
    );
  };

  // ── Channel tabs ───────────────────────────────────────────────────────

  const channels: { id: NotificationChannel; label: string; icon: React.FC<any> }[] = [
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'text', label: 'Text', icon: MessageSquare },
    { id: 'voice', label: 'Voice', icon: Phone },
  ];

  const textTabs: { id: TextTab; label: string }[] = [
    { id: 'appointment', label: 'Appointment' },
    { id: 'telehealth', label: 'Telehealth' },
    { id: 'rescheduled', label: 'Rescheduled' },
    { id: 'noshow', label: 'Missed appt' },
    { id: 'postsession', label: 'Post-session' },
    { id: 'document', label: 'Documents' },
    { id: 'cancellation', label: 'Cancellation' },
    { id: 'balance', label: 'Balance' },
  ];

  // ── Render: Email Channel ──────────────────────────────────────────────

  const renderEmailChannel = () => {
    const automatedEmails = DEFAULT_AUTOMATED_EMAILS.filter(e => e.category === 'automated');
    const reminderEmails = DEFAULT_AUTOMATED_EMAILS.filter(e => e.category === 'reminder');
    const billingEmails = DEFAULT_AUTOMATED_EMAILS.filter(e => e.category === 'billing');

    const renderEmailRow = (email: AutomatedEmail) => (
      <div
        key={email.id}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 0',
          borderBottom: '1px solid #1e3a5f',
        }}
      >
        <span style={{ fontSize: '14px', color: '#FFFFFF' }}>{email.label}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Toggle */}
          <button
            onClick={() => toggleEmail(email.id)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', padding: '4px',
            }}
            title={getEmailEnabled(email.id) ? 'Enabled' : 'Disabled'}
          >
            {getEmailEnabled(email.id) ? (
              <Check size={18} color="#10b981" />
            ) : (
              <X size={18} color="#64748b" />
            )}
          </button>
          {/* Edit icon */}
          {email.editable && (
            <button
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', padding: '4px',
              }}
              title="Edit template (coming soon)"
            >
              <Edit2 size={14} color="#6CA6C1" />
            </button>
          )}
        </div>
      </div>
    );

    const renderSection = (id: string, title: string, emails: AutomatedEmail[], extraContent?: React.ReactNode) => (
      <div key={id} style={{
        backgroundColor: '#113F67',
        border: '1px solid #6CA6C1',
        borderRadius: '8px',
        marginBottom: '16px',
        overflow: 'hidden',
      }}>
        <button
          onClick={() => setExpandedSection(expandedSection === id ? '' : id)}
          style={{
            width: '100%', padding: '16px 20px', border: 'none',
            backgroundColor: 'transparent', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}
        >
          <span style={{ fontSize: '16px', fontWeight: 600, color: '#FFFFFF' }}>{title}</span>
          <ChevronDown
            size={18} color="#87CEEB"
            style={{
              transform: expandedSection === id ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          />
        </button>
        {expandedSection === id && (
          <div style={{ padding: '0 20px 16px 20px' }}>
            {extraContent}
            {emails.map(renderEmailRow)}
          </div>
        )}
      </div>
    );

    return (
      <div>
        {/* Automated Emails */}
        {renderSection('automated', 'Automated emails', automatedEmails, (
          <p style={{ fontSize: '13px', color: '#87CEEB', margin: '0 0 16px 0' }}>
            You can view and customize the content of these templates.
          </p>
        ))}

        {/* Reminder Emails */}
        {renderSection('reminder', 'Reminder emails', reminderEmails, (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <span style={{ fontSize: '14px', color: '#FFFFFF' }}>Reminders are</span>
              <button
                onClick={() => {
                  const ns = { ...settings, reminderEmailsEnabled: !settings.reminderEmailsEnabled };
                  setSettings(ns);
                  saveSettings(ns);
                }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center',
                }}
              >
                {settings.reminderEmailsEnabled ? (
                  <ToggleRight size={28} color="#10b981" />
                ) : (
                  <ToggleLeft size={28} color="#64748b" />
                )}
              </button>
              <span style={{ fontSize: '14px', color: settings.reminderEmailsEnabled ? '#10b981' : '#64748b' }}>
                {settings.reminderEmailsEnabled ? 'on' : 'off'}
              </span>
            </div>
            {settings.reminderEmailsEnabled && (
              <div style={{
                backgroundColor: '#0A2540', borderRadius: '6px', padding: '12px',
                display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px',
              }}>
                <Info size={14} color="#87CEEB" />
                <span style={{ fontSize: '13px', color: '#87CEEB' }}>
                  Clients only receive reminders if they're also enabled at the client level.
                </span>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <span style={{ fontSize: '14px', color: '#FFFFFF' }}>Send email reminder</span>
              <select
                value={settings.reminderEmailTiming}
                onChange={(e) => {
                  const ns = { ...settings, reminderEmailTiming: parseInt(e.target.value) };
                  setSettings(ns);
                  saveSettings(ns);
                }}
                style={{
                  padding: '6px 12px', backgroundColor: '#0A2540', border: '1px solid #6CA6C1',
                  borderRadius: '4px', color: '#FFFFFF', fontSize: '14px',
                }}
              >
                <option value={24}>24 hours</option>
                <option value={48}>48 hours</option>
                <option value={72}>72 hours</option>
              </select>
              <span style={{ fontSize: '14px', color: '#FFFFFF' }}>before start time of appointment</span>
            </div>
            <p style={{ fontSize: '13px', color: '#87CEEB', margin: '0 0 8px 0' }}>
              In addition, an email reminder is sent 10 minutes before the start time of Telehealth appointments.
            </p>
            <p style={{ fontSize: '13px', color: '#87CEEB', margin: '0 0 16px 0' }}>
              Cancellation messages are sent upon the cancellation of an appointment.
            </p>
          </div>
        ))}

        {/* Billing Document Emails */}
        {renderSection('billing', 'Billing document emails', billingEmails, (
          <p style={{ fontSize: '13px', color: '#87CEEB', margin: '0 0 16px 0' }}>
            Customize the content of your billing document emails.
          </p>
        ))}
      </div>
    );
  };

  // ── Render: Text Channel ───────────────────────────────────────────────

  const renderTextChannel = () => {
    const currentTemplate = DEFAULT_TEXT_TEMPLATES.find(t => t.tab === textTab);
    if (!currentTemplate) return null;

    const currentContent = editingTemplate === currentTemplate.id
      ? templateDraft
      : getTemplateContent(currentTemplate.id);

    return (
      <div>
        {/* Text Reminders Toggle */}
        <div style={{
          backgroundColor: '#113F67', border: '1px solid #6CA6C1',
          borderRadius: '8px', padding: '20px', marginBottom: '16px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div>
              <span style={{ fontSize: '16px', fontWeight: 600, color: '#FFFFFF' }}>Text Reminders</span>
              <span style={{
                fontSize: '14px', color: settings.textRemindersEnabled ? '#10b981' : '#64748b',
                marginLeft: '12px',
              }}>
                {settings.textRemindersEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <button
              onClick={() => {
                const ns = { ...settings, textRemindersEnabled: !settings.textRemindersEnabled };
                setSettings(ns);
                saveSettings(ns);
              }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
              {settings.textRemindersEnabled ? (
                <ToggleRight size={32} color="#10b981" />
              ) : (
                <ToggleLeft size={32} color="#64748b" />
              )}
            </button>
          </div>

          <div style={{
            backgroundColor: '#0A2540', borderRadius: '6px', padding: '12px',
            display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px',
          }}>
            <Info size={14} color="#87CEEB" />
            <span style={{ fontSize: '13px', color: '#87CEEB' }}>
              Customize the content for your text reminders. Note: Clients only receive reminders if they're also enabled at the client level.
            </span>
          </div>
        </div>

        {/* Text Tabs */}
        <div style={{
          display: 'flex', gap: '4px', marginBottom: '16px', overflowX: 'auto',
          backgroundColor: '#0A2540', borderRadius: '8px', padding: '4px',
        }}>
          {textTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                setTextTab(tab.id);
                setEditingTemplate(null);
              }}
              style={{
                padding: '10px 16px', border: 'none', borderRadius: '6px',
                backgroundColor: textTab === tab.id ? '#28527A' : 'transparent',
                color: textTab === tab.id ? '#FFFFFF' : '#87CEEB',
                fontSize: '13px', fontWeight: textTab === tab.id ? 600 : 400,
                cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Timing */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          marginBottom: '20px', padding: '0 4px',
        }}>
          <Clock size={14} color="#87CEEB" />
          <span style={{ fontSize: '14px', color: '#FFFFFF' }}>Send text reminder</span>
          <select
            value={settings.textReminderTiming}
            onChange={(e) => {
              const ns = { ...settings, textReminderTiming: parseInt(e.target.value) };
              setSettings(ns);
              saveSettings(ns);
            }}
            style={{
              padding: '6px 12px', backgroundColor: '#113F67', border: '1px solid #6CA6C1',
              borderRadius: '4px', color: '#FFFFFF', fontSize: '14px',
            }}
          >
            <option value={1}>1 hour</option>
            <option value={2}>2 hours</option>
            <option value={4}>4 hours</option>
            <option value={12}>12 hours</option>
            <option value={24}>24 hours</option>
            <option value={48}>48 hours</option>
          </select>
          <span style={{ fontSize: '14px', color: '#FFFFFF' }}>before start time of appointment</span>
        </div>

        {/* Template Editor + Phone Preview */}
        <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
          {/* Template Editor */}
          <div style={{ flex: 1 }}>
            <div style={{
              backgroundColor: '#113F67', border: '1px solid #6CA6C1',
              borderRadius: '8px', padding: '20px',
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: '16px',
              }}>
                <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#FFFFFF', margin: 0 }}>
                  Customize text reminder
                </h3>
                {editingTemplate === currentTemplate.id ? (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => setEditingTemplate(null)}
                      style={{
                        padding: '6px 12px', backgroundColor: 'transparent',
                        border: '1px solid #64748b', borderRadius: '6px',
                        color: '#87CEEB', fontSize: '13px', cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => saveTemplate(currentTemplate.id, templateDraft)}
                      style={{
                        padding: '6px 12px', backgroundColor: '#6CA6C1',
                        border: 'none', borderRadius: '6px',
                        color: '#0A2540', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: '4px',
                      }}
                    >
                      <Save size={14} />
                      Save
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setEditingTemplate(currentTemplate.id);
                      setTemplateDraft(currentContent);
                    }}
                    style={{
                      padding: '6px 12px', backgroundColor: 'transparent',
                      border: '1px solid #6CA6C1', borderRadius: '6px',
                      color: '#6CA6C1', fontSize: '13px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: '4px',
                    }}
                  >
                    <Edit2 size={14} />
                    Edit
                  </button>
                )}
              </div>

              {editingTemplate === currentTemplate.id ? (
                <textarea
                  value={templateDraft}
                  onChange={(e) => setTemplateDraft(e.target.value)}
                  maxLength={currentTemplate.maxLength}
                  style={{
                    width: '100%', minHeight: '120px', padding: '12px',
                    backgroundColor: '#0A2540', border: '1px solid #6CA6C1',
                    borderRadius: '6px', color: '#FFFFFF', fontSize: '14px',
                    lineHeight: '1.5', resize: 'vertical', outline: 'none',
                    fontFamily: 'inherit',
                  }}
                />
              ) : (
                <div style={{
                  padding: '12px', backgroundColor: '#0A2540', borderRadius: '6px',
                  fontSize: '14px', color: '#e2e8f0', lineHeight: '1.5',
                  whiteSpace: 'pre-wrap', minHeight: '80px',
                }}>
                  {currentContent}
                </div>
              )}

              {/* Character count */}
              <div style={{
                display: 'flex', justifyContent: 'space-between', marginTop: '8px',
                fontSize: '12px', color: '#64748b',
              }}>
                <span>
                  {editingTemplate === currentTemplate.id ? templateDraft.length : currentContent.length}/{currentTemplate.maxLength}
                </span>
              </div>

              {/* Available Variables */}
              <div style={{ marginTop: '16px' }}>
                <h4 style={{ fontSize: '13px', color: '#87CEEB', marginBottom: '8px', fontWeight: 600 }}>
                  Available variables
                </h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {currentTemplate.variables.map(v => (
                    <button
                      key={v}
                      onClick={() => {
                        if (editingTemplate === currentTemplate.id) {
                          setTemplateDraft(prev => prev + v);
                        }
                      }}
                      style={{
                        padding: '4px 10px', backgroundColor: '#0A2540',
                        border: '1px solid #1e3a5f', borderRadius: '4px',
                        color: '#6CA6C1', fontSize: '12px', fontFamily: 'monospace',
                        cursor: editingTemplate === currentTemplate.id ? 'pointer' : 'default',
                        opacity: editingTemplate === currentTemplate.id ? 1 : 0.7,
                      }}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Phone Preview */}
          {renderPhonePreview(currentContent)}
        </div>
      </div>
    );
  };

  // ── Render: Voice Channel ──────────────────────────────────────────────

  const renderVoiceChannel = () => (
    <div style={{
      backgroundColor: '#113F67', border: '1px solid #6CA6C1',
      borderRadius: '8px', padding: '32px', textAlign: 'center',
    }}>
      <Phone size={32} color="#6CA6C1" style={{ marginBottom: '16px' }} />
      <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF', margin: '0 0 12px 0' }}>
        Voice Reminders
      </h3>
      <p style={{ fontSize: '14px', color: '#87CEEB', maxWidth: '400px', margin: '0 auto 20px auto' }}>
        MyClinicCore uses Hope AI for voice interactions. Voice appointment reminders can be configured here.
      </p>

      <div style={{
        backgroundColor: '#0A2540', borderRadius: '8px', padding: '20px',
        maxWidth: '400px', margin: '0 auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <span style={{ fontSize: '14px', color: '#FFFFFF' }}>Voice Reminders</span>
          <button
            onClick={() => {
              const ns = { ...settings, voiceRemindersEnabled: !settings.voiceRemindersEnabled };
              setSettings(ns);
              saveSettings(ns);
            }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          >
            {settings.voiceRemindersEnabled ? (
              <ToggleRight size={28} color="#10b981" />
            ) : (
              <ToggleLeft size={28} color="#64748b" />
            )}
          </button>
        </div>
        {settings.voiceRemindersEnabled && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '14px', color: '#87CEEB' }}>Send</span>
            <select
              value={settings.voiceReminderTiming}
              onChange={(e) => {
                const ns = { ...settings, voiceReminderTiming: parseInt(e.target.value) };
                setSettings(ns);
                saveSettings(ns);
              }}
              style={{
                padding: '6px 12px', backgroundColor: '#113F67', border: '1px solid #6CA6C1',
                borderRadius: '4px', color: '#FFFFFF', fontSize: '14px',
              }}
            >
              <option value={24}>24 hours</option>
              <option value={48}>48 hours</option>
            </select>
            <span style={{ fontSize: '14px', color: '#87CEEB' }}>before appointment</span>
          </div>
        )}
      </div>
    </div>
  );

  // ── Main Render ────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '32px', maxWidth: '960px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#FFFFFF', margin: '0 0 8px 0' }}>
            Client Notifications
          </h1>
          <p style={{ fontSize: '14px', color: '#87CEEB', margin: 0 }}>
            Automate confirmation and reminder emails, texts, and voice messages
          </p>
        </div>
        {(saving || saved) && (
          <div style={{
            padding: '8px 16px', borderRadius: '6px',
            backgroundColor: saved ? '#10b98120' : '#113F67',
            color: saved ? '#10b981' : '#87CEEB',
            fontSize: '13px', fontWeight: 500,
            display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            {saved ? <><Check size={14} /> Saved</> : 'Saving...'}
          </div>
        )}
      </div>

      {/* Channel Tabs */}
      <div style={{
        display: 'flex', gap: '0', marginBottom: '24px',
        borderBottom: '2px solid #1e3a5f',
      }}>
        {channels.map(ch => {
          const Icon = ch.icon;
          return (
            <button
              key={ch.id}
              onClick={() => setChannel(ch.id)}
              style={{
                padding: '12px 24px', border: 'none',
                backgroundColor: 'transparent',
                color: channel === ch.id ? '#6CA6C1' : '#87CEEB',
                fontSize: '15px', fontWeight: channel === ch.id ? 600 : 400,
                cursor: 'pointer',
                borderBottom: channel === ch.id ? '2px solid #6CA6C1' : '2px solid transparent',
                marginBottom: '-2px',
                display: 'flex', alignItems: 'center', gap: '8px',
                transition: 'all 0.2s',
              }}
            >
              <Icon size={18} />
              {ch.label}
            </button>
          );
        })}
      </div>

      {/* Channel Content */}
      {loading ? (
        <div style={{ padding: '48px', textAlign: 'center' }}>
          <p style={{ color: '#87CEEB' }}>Loading settings...</p>
        </div>
      ) : (
        <>
          {channel === 'email' && renderEmailChannel()}
          {channel === 'text' && renderTextChannel()}
          {channel === 'voice' && renderVoiceChannel()}
        </>
      )}
    </div>
  );
};

export default ClientNotifications;
